import { puzzleApi } from "@/services/api";

export type DailyPuzzlePageData = {
  puzzles: unknown[];
  nextPuzzleId: number | null;
};

const MAX_CACHED_PUZZLES = 8;
const resolvedPuzzles = new Map<string, DailyPuzzlePageData>();
const pendingPuzzles = new Map<string, Promise<DailyPuzzlePageData>>();

function normalizePuzzleId(id: string | number) {
  const normalizedId = String(id);
  return /^\d+$/.test(normalizedId) && Number(normalizedId) > 0
    ? normalizedId
    : null;
}

function normalizePuzzleResponse(response: any): DailyPuzzlePageData {
  const puzzles = response?.data?.puzzles;
  if (!Array.isArray(puzzles) || puzzles.length === 0) {
    throw new Error("Puzzle response did not contain any puzzles");
  }

  const candidateNextPuzzleId = Number(response?.data?.nextPuzzleId);
  return {
    puzzles,
    nextPuzzleId:
      Number.isInteger(candidateNextPuzzleId) && candidateNextPuzzleId > 0
        ? candidateNextPuzzleId
        : null,
  };
}

function cachePuzzle(id: string, data: DailyPuzzlePageData) {
  resolvedPuzzles.delete(id);
  resolvedPuzzles.set(id, data);

  while (resolvedPuzzles.size > MAX_CACHED_PUZZLES) {
    const oldestId = resolvedPuzzles.keys().next().value;
    if (oldestId === undefined) break;
    resolvedPuzzles.delete(oldestId);
  }
}

export function getPreloadedDailyPuzzle(id: string | number) {
  const normalizedId = normalizePuzzleId(id);
  return normalizedId ? resolvedPuzzles.get(normalizedId) ?? null : null;
}

export function preloadDailyPuzzle(
  id: string | number,
): Promise<DailyPuzzlePageData> {
  const normalizedId = normalizePuzzleId(id);
  if (!normalizedId) {
    return Promise.reject(new Error("Invalid puzzle id"));
  }

  const resolvedPuzzle = resolvedPuzzles.get(normalizedId);
  if (resolvedPuzzle) return Promise.resolve(resolvedPuzzle);

  const pendingPuzzle = pendingPuzzles.get(normalizedId);
  if (pendingPuzzle) return pendingPuzzle;

  const request = puzzleApi
    .get(`/puzzles/${normalizedId}`)
    .then(normalizePuzzleResponse)
    .then((data) => {
      cachePuzzle(normalizedId, data);
      return data;
    })
    .finally(() => {
      pendingPuzzles.delete(normalizedId);
    });

  pendingPuzzles.set(normalizedId, request);
  return request;
}
